import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Users, TrendingUp, AlertTriangle, ArrowUpRight, Wallet, Percent, CalendarClock } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import api from "@/lib/api";
import { fmtEUR, fmtDate, monthsFR, statusLabel } from "@/lib/format";
import { toast } from "sonner";

const PIE_COLORS = ["#18181B", "#71717A", "#A1A1AA", "#D4D4D8", "#52525B"];

function Kpi({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <Card className="card-flat p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-overline">{label}</div>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent || "bg-zinc-100"}`}>
          <Icon className="w-4 h-4 text-zinc-700" strokeWidth={2} />
        </div>
      </div>
      <div className="mt-4 data-value">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dashboard/stats").then(r => setD(r.data)).catch(e => toast.error("Erreur chargement: " + e.message));
  }, []);

  if (!d) return <div className="text-zinc-500 text-sm">Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-overline mb-1">Tableau de bord</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:'Outfit'}}>Vue d'ensemble</h1>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-properties" label="Biens" value={d.properties_total} sub={`${d.occupancy_rate}% occupation`} icon={Building2} />
        <Kpi testid="kpi-tenants" label="Locataires" value={d.tenants_total} icon={Users} />
        <Kpi testid="kpi-collected" label="Encaissé ce mois" value={fmtEUR(d.collected_this_month)} icon={Wallet} accent="bg-emerald-50" />
        <Kpi testid="kpi-late" label="Impayés" value={fmtEUR(d.late_amount)} sub={`${d.late_count} échéance(s)`} icon={AlertTriangle} accent="bg-red-50" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="card-flat p-6 lg:col-span-8" data-testid="chart-monthly">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-overline">Revenus</div>
              <h3 className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>Revenus mensuels (12 derniers mois)</h3>
            </div>
            <Badge variant="outline" className="gap-1"><TrendingUp className="w-3 h-3" /> Encaissé vs attendu</Badge>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.monthly_revenue}>
                <CartesianGrid strokeDasharray="2 4" stroke="#F4F4F5" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}€`} />
                <Tooltip contentStyle={{ background: "#09090B", border: "none", borderRadius: 6, color: "white", fontSize: 12 }} formatter={(v) => `${v} €`} />
                <Line type="monotone" dataKey="attendu" stroke="#A1A1AA" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="encaisse" stroke="#18181B" strokeWidth={2} dot={{ r: 3, fill: "#18181B" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="card-flat p-6 lg:col-span-4" data-testid="alerts-feed">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h3 className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>Alertes</h3>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {d.alerts.length === 0 && <div className="text-sm text-zinc-500">Aucune alerte. Tout est en ordre.</div>}
            {d.alerts.map((a, i) => (
              <div key={i} className={`text-sm p-3 rounded-md border ${a.severity === "danger" ? "bg-red-50 border-red-100 text-red-900" : "bg-amber-50 border-amber-100 text-amber-900"}`}>
                {a.message}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Lower row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="card-flat p-6 lg:col-span-7" data-testid="upcoming-due">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock className="w-4 h-4 text-zinc-700" />
            <h3 className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>Prochaines échéances</h3>
          </div>
          <div className="divide-y divide-zinc-200">
            {d.upcoming.length === 0 && <div className="text-sm text-zinc-500">Aucune échéance à venir.</div>}
            {d.upcoming.map((p) => (
              <div key={p.id} className="py-2.5 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{monthsFR[p.period_month]} {p.period_year}</div>
                  <div className="text-xs text-zinc-500">Échéance: {fmtDate(p.due_date)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm">{fmtEUR(p.amount_due)}</span>
                  <Badge className={`status-${p.status} border`} variant="outline">{statusLabel[p.status]}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="card-flat p-6 lg:col-span-5" data-testid="unpaid-distribution">
          <div className="flex items-center gap-2 mb-4">
            <Percent className="w-4 h-4 text-zinc-700" />
            <h3 className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>Répartition des impayés</h3>
          </div>
          {d.unpaid_distribution.length === 0 ? (
            <div className="h-60 flex items-center justify-center text-sm text-zinc-500">Aucun impayé. <ArrowUpRight className="w-3 h-3 ml-1" /></div>
          ) : (
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.unpaid_distribution} dataKey="value" nameKey="name" outerRadius={80} innerRadius={40}>
                    {d.unpaid_distribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#09090B", border: "none", borderRadius: 6, color: "white", fontSize: 12 }} formatter={(v) => `${v} €`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
